import type { EmergencyPreparednessFrameworkResult } from '../types.js';
import type { S16Intake } from './s16Workflow.js';

export const S16_VERIFICATION_ITEMS = [
    ['evacuationRoutes', 'Evacuation routes and site access'], ['rallyPoints', 'Rally points and personnel accountability'],
    ['equipmentRequirements', 'Equipment availability and suitability'], ['tacticalChecklists', 'Role duties and named response team'],
    ['emergencyContacts', 'Emergency contacts and communications'], ['assistancePlan', 'Assistance and medical response'],
] as const;
const text = (value: unknown, maximum = 16000) => String(value ?? '').trim().slice(0, maximum);

export function cleanS16Report(source: any, intake: S16Intake, preserveVerification = false): EmergencyPreparednessFrameworkResult {
    const raw = source && typeof source === 'object' ? source : {};
    return { reportType: 'EmergencyPreparednessFrameworkResult', workflowVersion: 'EPP-16-v1',
        organizationName: intake.organizationName, industry: intake.industry, scenario: intake.scenario,
        affectedLocations: intake.sites, siteDetails: intake.siteDetails, evacuationRoutes: intake.evacuationRoutes,
        rallyPoints: intake.rallyPoints, equipmentRequirements: intake.equipmentRequirements,
        tacticalChecklists: intake.tacticalChecklists, responseTeam: intake.responseTeam,
        activationTriggers: intake.activationTriggers, emergencyContacts: intake.emergencyContacts,
        communicationsPlan: intake.communicationsPlan, assistancePlan: intake.assistancePlan,
        reviewFrequency: intake.reviewFrequency, exerciseType: intake.exerciseType,
        executiveSummary: text(raw.executiveSummary), scenarioAssessment: text(raw.scenarioAssessment),
        responseProcedures: text(raw.responseProcedures), accountability: text(raw.accountability),
        trainingMaintenance: text(raw.trainingMaintenance),
        verification: Object.fromEntries(S16_VERIFICATION_ITEMS.map(([key]) => [key, preserveVerification && raw.verification?.[key] === true])),
        evidenceGaps: [...new Set((Array.isArray(raw.evidenceGaps) ? raw.evidenceGaps : []).slice(0, 25)
            .map((item: unknown): string => text(item, 500)).filter(Boolean))] as string[],
        timestamp: text(raw.timestamp, 50) || new Date().toISOString() };
}

export function missingS16Report(report: EmergencyPreparednessFrameworkResult): string[] {
    const missing = ([['Executive Summary', report.executiveSummary], ['Scenario Assessment', report.scenarioAssessment],
        ['Response Procedures', report.responseProcedures], ['Personnel Accountability', report.accountability],
        ['Training and Maintenance', report.trainingMaintenance]] as const).filter(([, value]) => !text(value)).map(([label]) => String(label));
    if (!report.affectedLocations.length || !report.scenario || !report.evacuationRoutes.length || !report.rallyPoints.length
        || !report.equipmentRequirements.length || !report.tacticalChecklists.length
        || report.tacticalChecklists.some(item => !item.role || !item.tasks.length))
        missing.push('Emergency response decisions');
    return missing;
}

export const missingS16Verification = (report: EmergencyPreparednessFrameworkResult) => S16_VERIFICATION_ITEMS
    .filter(([key]) => report.verification?.[key] !== true).map(([, label]) => label);

export function s16ReviewError(previous: string, next: string, report: EmergencyPreparednessFrameworkResult, changed: boolean): string | null {
    if (next === 'Approved' && previous !== 'Validated')
        return 'Validate the S-16 plan before approval and sign-off.';
    const missing = missingS16Report(report);
    if (next !== 'Draft' && missing.length)
        return `Complete the report: ${missing.slice(0, 3).join(', ')}.`;
    if (next !== 'Draft' && missingS16Verification(report).length)
        return 'Complete every emergency readiness check before validation or approval.';
    if (next === 'Approved' && changed)
        return 'Save and validate report changes before approval.';
    return null;
}

export function s16DeliveryLink(requestId: string, reference: string) {
    const id = `service-${requestId}-s16`;
    return { id, sourceId: id, title: `${reference} Emergency Preparedness Plan`, type: 'EmergencyPreparednessFrameworkResult' as const };
}
